/**
 * PolicyViewerDialog — read-only in-app viewer for policy documents.
 * Downloads are disabled; documents are streamed inline and rendered in an iframe.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const VIEWABLE = (type) =>
  type === 'application/pdf' || type?.startsWith('text/') || type?.startsWith('image/');

export const PolicyViewerDialog = ({ policy, onClose }) => {
  const [state, setState] = useState({ loading: false, url: null, type: null, error: null });

  useEffect(() => {
    if (!policy) return;
    let objectUrl = null;
    setState({ loading: true, url: null, type: null, error: null });
    axios
      .get(`${API}/policies/${policy.id}/view`, { responseType: 'blob' })
      .then((res) => {
        const type = res.data.type || res.headers['content-type'];
        objectUrl = URL.createObjectURL(res.data);
        setState({ loading: false, url: objectUrl, type, error: null });
      })
      .catch(async (err) => {
        let msg = 'Failed to load the document';
        try {
          const text = await err?.response?.data?.text?.();
          msg = JSON.parse(text)?.detail || msg;
        } catch { /* keep default */ }
        setState({ loading: false, url: null, type: null, error: msg });
      });
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [policy]);

  return (
    <Dialog open={!!policy} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0" data-testid="policy-viewer-dialog">
        <DialogHeader className="px-5 py-3.5 border-b border-slate-200 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base pr-8">
            <FileText className="h-4.5 w-4.5 text-blue-600 shrink-0" />
            <span className="truncate" data-testid="policy-viewer-title">{policy?.title}</span>
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100 text-[11px] shrink-0">
              Read-only
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {policy?.category ? `${policy.category} · ` : ''}
            {policy?.version_label || policy?.current_version_label
              ? `v${policy.version_label || policy.current_version_label} · ` : ''}
            Viewing only — downloading policy documents is disabled.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-100">
          {state.loading && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500" data-testid="policy-viewer-loading">
              <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
              <p className="text-sm">Loading document…</p>
            </div>
          )}
          {!state.loading && state.error && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 px-8 text-center" data-testid="policy-viewer-error">
              <EyeOff className="h-8 w-8 text-slate-300" />
              <p className="text-sm">{state.error}</p>
            </div>
          )}
          {!state.loading && !state.error && state.url && (
            VIEWABLE(state.type) ? (
              <iframe
                src={state.type === 'application/pdf' ? `${state.url}#toolbar=0&navpanes=0` : state.url}
                title={policy?.title || 'Policy document'}
                className="w-full h-full border-0"
                data-testid="policy-viewer-frame"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 px-8 text-center" data-testid="policy-viewer-unsupported">
                <FileText className="h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">Inline preview is not available for this file type.</p>
                <p className="text-xs">Policy downloads are disabled. Please contact HR if you need help accessing this policy.</p>
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyViewerDialog;
